import { useEffect } from "react";

export const SITE_URL = "https://www.travel-time.ro";
export const DEFAULT_IMAGE = `${SITE_URL}/images/og-travel-time.jpg`;
export const ORGANIZATION_ID = `${SITE_URL}/#organization`;

const setMeta = (attr, key, content) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!content) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setCanonical = (href) => {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!href) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const setJsonLd = (data) => {
  let el = document.getElementById("page-jsonld");
  if (!data) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = "page-jsonld";
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
};

// Scurtează un text la ~155 caractere: la sfârșitul unei propoziții dacă se
// poate, altfel la ultimul cuvânt întreg, cu „…”
export const toDescription = (text, max = 155) => {
  const clean = (text || "").replace(/\s+/g, " ").trim();
  if (clean.length <= max) return clean;
  const head = clean.slice(0, max + 1);
  const sentenceEnd = Math.max(
    head.lastIndexOf(". "),
    head.lastIndexOf("! "),
    head.lastIndexOf("? ")
  );
  if (sentenceEnd >= 80) return head.slice(0, sentenceEnd + 1);
  const cut = clean.slice(0, clean.lastIndexOf(" ", max - 1));
  return /[.!?]$/.test(cut) ? cut : cut.replace(/[,;:]$/, "") + "…";
};

export const breadcrumbJsonLd = (items) => ({
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: item.name,
    item: `${SITE_URL}${item.path}`,
  })),
});

/**
 * Setează în <head> titlul, descrierea, canonical-ul, Open Graph și JSON-LD
 * pentru pagina curentă. Prerender-ul (scripts/prerender.js) salvează rezultatul
 * ca HTML static pentru crawlere.
 *
 * path: calea canonică, ex. "/corporate" (null pentru pagini fără canonical)
 * jsonLd: obiect sau listă de obiecte schema.org, fără "@context"
 */
export const useSeo = (seo) => {
  const key = seo ? JSON.stringify(seo) : "";

  useEffect(() => {
    if (!seo) return;
    const {
      title,
      description,
      path,
      lang = "ro",
      image = DEFAULT_IMAGE,
      type = "website",
      jsonLd,
      noindex = false,
    } = seo;
    const url = path ? `${SITE_URL}${path}` : null;

    document.title = title;
    document.documentElement.lang = lang;
    setMeta("name", "description", description);
    setMeta("name", "robots", noindex ? "noindex, follow" : null);
    setCanonical(noindex ? null : url);

    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", url);
    setMeta("property", "og:type", type);
    setMeta("property", "og:image", image);
    setMeta("property", "og:locale", lang === "en" ? "en_US" : "ro_RO");
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", image);

    // Imaginea implicită are dimensiuni cunoscute; pentru altele nu le declarăm
    const isDefaultImage = image === DEFAULT_IMAGE;
    setMeta("property", "og:image:width", isDefaultImage ? "1200" : null);
    setMeta("property", "og:image:height", isDefaultImage ? "630" : null);
    setMeta("property", "og:image:alt", isDefaultImage ? title : null);

    const graph = jsonLd ? [].concat(jsonLd) : [];
    setJsonLd(
      graph.length ? { "@context": "https://schema.org", "@graph": graph } : null
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
};
